// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import resendActivationEmail from "@mcmc/App/Handlers/AccountActivation/ResendActivationEmail";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    linkInvalidMessage = "linkInvalidMessage",
}

interface ResendActivationEmailWidgetProps extends WidgetProps {
    fields: {
        [fields.linkInvalidMessage]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ token: string }>(location.search);

    return {
        token: parsedQuery.token,
        homePageUrl: getHomePageUrl(state),
    };
};

const mapDispatchToProps = {
    resendActivationEmail,
};

type Props = ResendActivationEmailWidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const ResendActivationEmail = ({
    resendActivationEmail,
    token,
    homePageUrl,
    fields: { linkInvalidMessage },
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [isSending, setIsSending] = useState(false);
    const history = useHistory();

    const requestActivationEmail = () => {
        if (!token) {
            return;
        }

        setIsSending(true);
        resendActivationEmail({
            token,
            onSuccess: () => {
                toasterContext.addToast({
                    body: siteMessage("McMc_Resend_Activation_Success"),
                    messageType: "success",
                    timeoutLength: 5000,
                });
            },
            onError: error => {
                toasterContext.addToast({
                    body: error ?? siteMessage("McMc_Resend_Activation_Error"),
                    messageType: "danger",
                    timeoutLength: 5000,
                });
            },
            onComplete: () => {
                setIsSending(false);
                history.push(homePageUrl);
            },
        });
    };

    return (
        <LoadingOverlay
            loading={isSending}
            css={css`
                min-height: 300px;
            `}
        >
            {linkInvalidMessage ? <Typography>{linkInvalidMessage}&nbsp;</Typography> : null}
            <Link onClick={() => requestActivationEmail()}>{translate("Resend Activation")}.</Link>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ResendActivationEmail),
    definition: {
        group: "Common",
        allowedContexts: ["ExpiredAccountActivationLinkPage"],
        fieldDefinitions: [
            {
                name: fields.linkInvalidMessage,
                displayName: "Activation Link Invalid Message",
                editorTemplate: "MultilineTextField",
                defaultValue:
                    "The link you used for activating your account is invalid. Please get a new link by trying to",
                fieldType: "Contextual",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
